import { useEffect, useState } from 'react';
import { guestApi, type GuestListItem } from '../api';
import { FONT_UI, RADIUS, useTheme } from '../theme';
import { Button } from './ui';

// GU1 · Deltagarna i jakten ser gäster som väntar (kod/QR utan inloggning) och släpper in eller nekar.
// Pushnotisen (API: routes/guestJoin.ts) leder hit; listan hämtas var femte sekund medan jakten pågår.

export function GuestRequests({ meetingId }: { meetingId: string }) {
  const T = useTheme();
  const [guests, setGuests] = useState<GuestListItem[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdmitted, setShowAdmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => guestApi.list(meetingId).then(g => { if (!cancelled) setGuests(g); }).catch(() => {});
    void load();
    const tid = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [meetingId]);

  async function decide(g: GuestListItem, approve: boolean) {
    if (!approve && g.status === 'approved' && !confirm(`Ta bort ${g.name} ur jakten?`)) return;
    setBusy(g.id);
    try {
      await guestApi.decide(meetingId, g.id, approve);
      setGuests(list => approve ? list.map(x => (x.id === g.id ? { ...x, status: 'approved' } : x)) : list.filter(x => x.id !== g.id));
    } catch (e) {
      alert((e as Error).message || 'Det gick inte');
      setGuests(list => list.filter(x => x.id !== g.id));
    }
    setBusy(null);
  }

  const pending = guests.filter(g => g.status === 'pending');
  const admitted = guests.filter(g => g.status === 'approved');
  if (!pending.length && !admitted.length) return null;

  return (
    <div style={{ position: 'fixed', top: 'calc(64px + env(safe-area-inset-top, 0px))', left: 12, right: 12, zIndex: 20050, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none', fontFamily: FONT_UI }}>
      {pending.map(g => (
        <div key={g.id} role="alertdialog" aria-label={`${g.name} vill ansluta`} style={{ pointerEvents: 'auto', width: '100%', maxWidth: 420, background: T.surface, color: T.text, border: `1px solid ${T.amber}`, borderRadius: RADIUS.lg, boxShadow: T.shadow.raised, padding: '12px 14px' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{g.name} vill ansluta som gäst</div>
          <div style={{ color: T.textSub, fontSize: 13, margin: '2px 0 10px' }}>Gästen ser kartan, radion och jaktchatten tills jakten avslutas.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" style={{ flex: 1 }} loading={busy === g.id} onClick={() => void decide(g, true)}>Godkänn</Button>
            <Button size="sm" variant="ghost" style={{ flex: 1 }} disabled={busy === g.id} onClick={() => void decide(g, false)}>Neka</Button>
          </div>
        </div>
      ))}
      {admitted.length > 0 && (
        <div style={{ pointerEvents: 'auto', alignSelf: 'flex-start', background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.lg, padding: '6px 10px', fontSize: 13, color: T.textSub, boxShadow: T.shadow.raised }}>
          <button type="button" onClick={() => setShowAdmitted(v => !v)} style={{ background: 'none', border: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', padding: 0 }}>
            Gäster i jakten: {admitted.length} {showAdmitted ? '▴' : '▾'}
          </button>
          {showAdmitted && admitted.map(g => (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, color: T.text }}>
              <span style={{ flex: 1 }}>{g.name}</span>
              <Button size="sm" variant="ghost" disabled={busy === g.id} onClick={() => void decide(g, false)}>Ta bort</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
